import React, { useEffect, useState } from 'react';
import Loader from '../components/Loader';
import Calling from '../api/Calling';
import { toast } from 'react-toastify';
import UserChart from '../components/charts/UserChart';
import LoginChart from '../components/charts/LoginChart';

import { FaRegUser } from 'react-icons/fa';
import { RiLoginBoxLine } from "react-icons/ri";
import { MdDelete, MdOutlinePayments } from "react-icons/md";
import { FiFilter, FiCalendar, FiRefreshCw } from "react-icons/fi";
import { HiOutlineUserGroup } from "react-icons/hi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { useNavigate } from 'react-router-dom';
import MainButton from '../components/ul/MainButton';
import getISTDate from '../components/getISTDate';
import PlanHistoryChart from '../components/charts/Planhistorychart';
import ReferralChart from '../components/charts/ReferralChart';
import { TbLayoutDashboardFilled } from 'react-icons/tb';

const Home = () => {
    const [loader, setLoader] = useState(false);
    const [userData, setUserData] = useState([]);
    const [loginData, setLogin] = useState([]);
    const [androidLoginData, setAndroidLoginData] = useState([]);
    const [iosLoginData, setIosLoginData] = useState([]);
    const [planHistoryData, setPlanHistoryData] = useState([]);
    const [activePlans, setActivePlans] = useState([]);
    const [expiredPlans, setExpiredPlans] = useState([]);

    // States for referral and delete request
    const [referralData, setReferralData] = useState([]);
    const [referralPlanData, setReferralPlanData] = useState([]);
    const [activeReferralPlans, setActiveReferralPlans] = useState([]);
    const [expiredReferralPlans, setExpiredReferralPlans] = useState([]);
    const [deleteRequestCount, setDeleteRequestCount] = useState(0);

    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());

    const [selectedBtn, setSelectedBtn] = useState('today');
    const [selectShowType, setSelectShowType] = useState('users');
    const [dataFetchError, setDataFetchError] = useState(null);

    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [isRefreshing, setIsRefreshing] = useState(false);

    const navigate = useNavigate();

    const fetchData = async () => {
        setLoader(true);
        setDataFetchError(null);

        try {
            let dataTypes;
            if (selectShowType === 'all') {
                dataTypes = 'users,login,planHistory,referral,referralPlan';
            } else {
                dataTypes = selectShowType;
            }

            const response = await Calling.adminDashbord(startDate, endDate, dataTypes);

            if (response?.status) {
                setUserData(response?.totalUser || []);
                const totalLogin = response?.totalLogin || [];
                setLogin(totalLogin);

                const androidLogins = totalLogin.filter(login => login.l_platform === 'android');
                const iosLogins = totalLogin.filter(login => login.l_platform === 'ios');
                setAndroidLoginData(androidLogins);
                setIosLoginData(iosLogins);

                const planHistory = response?.planHistory || [];
                setPlanHistoryData(planHistory);

                const active = planHistory.filter(plan => plan.status === 'active');
                const expired = planHistory.filter(plan => plan.status === 'expired');
                setActivePlans(active);
                setExpiredPlans(expired);

                setReferralData(response?.totalReferral || []);

                const referralPlans = response?.totalReferralPlan || [];
                setReferralPlanData(referralPlans);

                const activeRefPlans = referralPlans.filter(plan => plan.status === 'active');
                const expiredRefPlans = referralPlans.filter(plan => plan.status === 'expired');
                setActiveReferralPlans(activeRefPlans);
                setExpiredReferralPlans(expiredRefPlans);

                setDeleteRequestCount(response?.deleteAccount || 0);

                setLastUpdated(new Date());
            } else {
                toast.error(response?.message || 'Failed to fetch data');
                setDataFetchError(response?.message || 'Failed to fetch data');
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to fetch dashboard data');
            setDataFetchError('Failed to fetch dashboard data');
        } finally {
            setLoader(false);
            setIsRefreshing(false);
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        await fetchData();
    };

    useEffect(() => {
        fetchData();
    }, [startDate, endDate, selectShowType]);

    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();

        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000;
            const istTime = new Date(date.getTime() + istOffset);
            return istTime.toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectedBtn('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectedBtn('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);

                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectedBtn('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);

                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectedBtn('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);

                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectedBtn('last6Months');
                break;

            case 'thisYear':
                start = toIST(new Date(todayUTC.getFullYear(), 0, 1));
                end = toIST(new Date(todayUTC.getFullYear(), 11, 31));
                setSelectedBtn('thisYear');
                break;

            case 'lastYear':
                start = toIST(new Date(todayUTC.getFullYear() - 1, 0, 1));
                end = toIST(new Date(todayUTC.getFullYear() - 1, 11, 31));
                setSelectedBtn('lastYear');
                break;

            default:
                start = '';
                end = '';
                setSelectedBtn('range');
        }

        setStartDate(start);
        setEndDate(end);
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchData();
    };

    const handleFormDate = (e) => {
        if (e.target.name === "startDate") {
            setStartDate(e.target.value);
            setSelectedBtn('range');
        } else {
            setEndDate(e.target.value);
            setSelectedBtn('range');
        }
    };

    const handleDataTypeChange = (type) => {
        setSelectShowType(type);
    };

    const calculateTotalRevenue = () => {
        return planHistoryData.reduce((sum, plan) => sum + (plan.p_price || 0), 0);
    };

    // Component for data type filtering
    const DataTypeFilter = () => {
        return (
            <div className="p-4 md:p-6 bg-white border-t border-main-border">
                <h3 className="text-base md:text-lg font-semibold text-main-font mb-3 md:mb-4 flex items-center">
                    <FiFilter className="mr-2 text-main-blue" /> Data Type Filters
                </h3>
                <div className="flex flex-wrap gap-2 md:gap-3">
                    <MainButton onClick={() => handleDataTypeChange('all')} label={'All'} selectState={selectShowType === 'all'} />
                    <MainButton onClick={() => handleDataTypeChange('users')} label={'Users'} selectState={selectShowType === 'users'} />
                    <MainButton onClick={() => handleDataTypeChange('login')} label={'Logins'} selectState={selectShowType === 'login'} />
                    <MainButton onClick={() => handleDataTypeChange('planHistory')} label={'Plan History'} selectState={selectShowType === 'planHistory'} />
                    <MainButton onClick={() => handleDataTypeChange('referral')} label={'Referral'} selectState={selectShowType === 'referral'} />
                    <MainButton onClick={() => handleDataTypeChange('referralPlan')} label={'Referral Plan'} selectState={selectShowType === 'referralPlan'} />
                </div>
            </div>
        );
    };

    // No Data Component
    const NoDataFound = ({ title, message }) => {
        return (
            <div className="w-full flex flex-col items-center justify-center py-12 md:py-16 px-4">
                <div className="bg-gray-100 rounded-full p-4 md:p-6 mb-4">
                    <svg className="w-12 h-12 md:w-16 md:h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                </div>
                <h3 className="text-lg md:text-xl font-semibold text-gray-700 mb-2 text-center">{title || 'No Data Found'}</h3>
                <p className="text-sm md:text-base text-gray-500 text-center max-w-md">
                    {message || 'No records available for the selected date range. Try adjusting your filters or selecting a different time period.'}
                </p>
            </div>
        );
    };

    const getCurrentData = () => {
        switch (selectShowType) {
            case 'users':
                return userData;
            case 'login':
                return loginData;
            case 'planHistory':
                return planHistoryData;
            case 'referral':
                return referralData;
            case 'referralPlan':
                return referralPlanData;
            case 'all':
                return [...userData, ...loginData, ...planHistoryData, ...referralData, ...referralPlanData];
            default:
                return [];
        }
    };

    const getNoDataMessage = () => {
        const messages = {
            users: { title: 'No Users Found', message: 'No user registrations found for the selected date range.' },
            login: { title: 'No Logins Found', message: 'No login records found for the selected date range.' },
            planHistory: { title: 'No Plan History', message: 'No plan purchase history found for the selected date range.' },
            referral: { title: 'No Referrals Found', message: 'No referral records found for the selected date range.' },
            referralPlan: { title: 'No Referral Plans', message: 'No referral plan history found for the selected date range.' },
            all: { title: 'No Data Available', message: 'No records found for the selected date range. Try adjusting your filters.' }
        };
        return messages[selectShowType] || messages.all;
    };

    const renderCharts = () => {
        const showUserChart = selectShowType === 'all' || selectShowType === 'users';
        const showLoginChart = selectShowType === 'all' || selectShowType === 'login';
        const showPlanHistoryChart = selectShowType === 'all' || selectShowType === 'planHistory';
        const showReferralChart = selectShowType === 'all' || selectShowType === 'referral';
        const showReferralPlanChart = selectShowType === 'all' || selectShowType === 'referralPlan';

        const chartSections = [];

        if (showUserChart && userData.length > 0) {
            chartSections.push({ type: 'user', component: <UserChart userData={userData} /> });
        }
        if (showLoginChart && androidLoginData.length > 0) {
            chartSections.push({ type: 'androidLogin', component: <LoginChart data={androidLoginData} title="Android Logins Over Time" /> });
        }
        if (showLoginChart && iosLoginData.length > 0) {
            chartSections.push({ type: 'iosLogin', component: <LoginChart data={iosLoginData} title="iOS Logins Over Time" /> });
        }
        if (showPlanHistoryChart && planHistoryData.length > 0) {
            chartSections.push({ type: 'planHistory', component: <PlanHistoryChart data={planHistoryData} /> });
        }
        if (showReferralChart && referralData.length > 0) {
            chartSections.push({ type: 'referral', component: <ReferralChart data={referralData} title="Referrals Over Time" /> });
        }
        if (showReferralPlanChart && referralPlanData.length > 0) {
            chartSections.push({ type: 'referralPlan', component: <ReferralChart data={referralPlanData} title="Referral Plans Over Time" /> });
        }

        if (chartSections.length === 0) {
            return null;
        }

        const isSingleChart = chartSections.length === 1;

        if (isSingleChart) {
            return (
                <div className="mt-6">
                    <div className="w-full max-w-4xl mx-auto">
                        {chartSections[0].component}
                    </div>
                </div>
            );
        }

        return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mt-6">
                {chartSections.map((chart) => (
                    <div key={chart.type}>
                        {chart.component}
                    </div>
                ))}
            </div>
        );
    };

    const renderCards = () => {
        const cardConfigs = [
            {
                show: selectShowType === 'all' || selectShowType === 'users',
                data: userData.length,
                label: 'Users',
                icon: <FaRegUser size={22} />,
                borderColor: 'border-teal-200',
                iconBgColor: 'bg-teal-600',
                onClick: () => navigate('/manage/user/all/data')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'login',
                data: loginData.length,
                label: 'Total Login',
                icon: <RiLoginBoxLine size={24} />,
                borderColor: 'border-yellow-200',
                iconBgColor: 'bg-yellow-500',
                onClick: () => navigate('/manage/user/login/all/data')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'login',
                data: androidLoginData.length,
                label: 'Android Login',
                icon: <RiLoginBoxLine size={24} />,
                borderColor: 'border-green-200',
                iconBgColor: 'bg-green-500',
                onClick: () => navigate('/manage/user/login/android/data')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'login',
                data: iosLoginData.length,
                label: 'iOS Login',
                icon: <RiLoginBoxLine size={24} />,
                borderColor: 'border-blue-200',
                iconBgColor: 'bg-blue-500',
                onClick: () => navigate('/manage/user/login/ios/data')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'planHistory',
                data: planHistoryData.length,
                label: 'Total Plans',
                icon: <MdOutlinePayments size={24} />,
                borderColor: 'border-purple-200',
                iconBgColor: 'bg-purple-500',
                onClick: () => navigate('/manage/plan/history')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'planHistory',
                data: activePlans.length,
                label: 'Active Plans',
                icon: <MdOutlinePayments size={24} />,
                borderColor: 'border-emerald-200',
                iconBgColor: 'bg-emerald-500',
                onClick: () => navigate('/manage/plan/history')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'planHistory',
                data: expiredPlans.length,
                label: 'Expired Plans',
                icon: <MdOutlinePayments size={24} />,
                borderColor: 'border-red-200',
                iconBgColor: 'bg-red-500',
                onClick: () => navigate('/manage/plan/history')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'planHistory',
                data: `₹${calculateTotalRevenue()}`,
                label: 'Total Revenue',
                icon: <MdOutlinePayments size={24} />,
                borderColor: 'border-indigo-200',
                iconBgColor: 'bg-indigo-500',
                onClick: () => navigate('/manage/plan/history')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'referral',
                data: referralData.length,
                label: 'Total Referrals',
                icon: <HiOutlineUserGroup size={24} />,
                borderColor: 'border-cyan-200',
                iconBgColor: 'bg-cyan-500',
                onClick: () => navigate('/manage/user/referral/all/data')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'referralPlan',
                data: referralPlanData.length,
                label: 'Total Referral Plans',
                icon: <HiOutlineUserGroup size={24} />,
                borderColor: 'border-orange-200',
                iconBgColor: 'bg-orange-500',
                onClick: () => navigate('/manage/referral/plan/history')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'referralPlan',
                data: activeReferralPlans.length,
                label: 'Active Referral Plans',
                icon: <HiOutlineUserGroup size={24} />,
                borderColor: 'border-lime-200',
                iconBgColor: 'bg-lime-500',
                onClick: () => navigate('/manage/referral/plan/history')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'referralPlan',
                data: expiredReferralPlans.length,
                label: 'Expired Referral Plans',
                icon: <HiOutlineUserGroup size={24} />,
                borderColor: 'border-rose-200',
                iconBgColor: 'bg-rose-500',
                onClick: () => navigate('/manage/referral/plan/history')
            },
        ];

        const visibleCards = cardConfigs.filter(card => card.show);

        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                {visibleCards.map((card, index) => (
                    <div
                        key={index}
                        onClick={card.onClick}
                        className={`
                            group relative p-5 rounded-2xl cursor-pointer
                            bg-white border ${card.borderColor}
                            shadow-sm hover:shadow-xl 
                            transition-all duration-300 
                            hover:-translate-y-1.5 
                            hover:border-main-blue
                        `}
                    >
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-main-blue to-transparent opacity-0 group-hover:opacity-10 blur-lg rounded-2xl transition duration-300"></div>

                        <div className="relative flex justify-between items-center">
                            <div>
                                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                    {card.label}
                                </p>
                                <h4 className="text-3xl font-bold tracking-tight mt-1 text-gray-800">
                                    {card.data}
                                </h4>
                            </div>

                            <div
                                className={`
                                    flex items-center justify-center rounded-2xl
                                    p-3 shadow-md
                                    ${card.iconBgColor}
                                    text-white
                                    transition-all duration-300
                                    transform group-hover:scale-110
                                `}
                            >
                                {card.icon}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const hasData = () => {
        const currentData = getCurrentData();
        return currentData.length > 0;
    };

    return (
        <>
            {loader ? (
                <Loader />
            ) : (
                <div className='w-full text-main-font'>
                    {/* Header Section */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-2 gap-3">
                        {/* Dashboard Title with Delete Request Badge */}
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-bold flex items-center gap-3 text-gray-800">
                                <div className="relative p-2.5 bg-main-blue rounded-xl shadow-lg">
                                    <TbLayoutDashboardFilled className="text-white text-xl" />

                                    {deleteRequestCount > 0 && (
                                        <button
                                            onClick={() => navigate('/manage/user/all/delete')}
                                            className="
                                                absolute -top-2 -right-2
                                                min-w-[20px] h-5
                                                bg-red-500 text-white
                                                text-[10px] font-semibold
                                                rounded-full
                                                flex items-center justify-center
                                                shadow-md
                                                hover:bg-red-600
                                                transition-all duration-200
                                            "
                                            title="View Delete Requests"
                                        >
                                            {deleteRequestCount > 99 ? '99+' : deleteRequestCount}
                                        </button>
                                    )}
                                </div>

                                <span>Dashboard</span>
                            </h1>
                        </div>

                        {/* Actions - Last Updated & Refresh */}
                        <div className="flex items-center gap-3">
                            <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-200 text-sm text-gray-600 flex items-center gap-2">
                                <FiCalendar className="text-gray-500" size={16} />
                                <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
                            </div>
                            <button
                                onClick={handleRefresh}
                                disabled={isRefreshing}
                                className={`bg-main-blue hover:bg-main-blue/80 text-white px-4 py-2 rounded-lg shadow-sm border border-main-blue text-sm font-medium flex items-center gap-2 transition-all duration-200 ${isRefreshing ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-md'}`}
                                title="Refresh Dashboard"
                            >
                                <FiRefreshCw className={`${isRefreshing ? 'animate-spin' : ''}`} size={16} />
                            </button>
                        </div>
                    </div>

                    {/* Date Range Filters */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="p-6">
                            <div className="flex items-center mb-4">
                                <FiCalendar className="text-main-blue mr-2" />
                                <h2 className="text-lg font-semibold text-gray-800">Date Range</h2>
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-8 flex flex-wrap gap-3">
                                    <MainButton onClick={() => handleDateRangeFilter('today')} label={'Today'} selectState={selectedBtn === "today"} />
                                    <MainButton onClick={() => handleDateRangeFilter('yesterday')} label={'Yesterday'} selectState={selectedBtn === "yesterday"} />
                                    <MainButton onClick={() => handleDateRangeFilter('thisWeek')} label={'This Week'} selectState={selectedBtn === "thisWeek"} />
                                    <MainButton onClick={() => handleDateRangeFilter('thisMonth')} label={'This Month'} selectState={selectedBtn === "thisMonth"} />
                                    <MainButton onClick={() => handleDateRangeFilter('last6Months')} label={'Last 6 Months'} selectState={selectedBtn === "last6Months"} />
                                    <MainButton onClick={() => handleDateRangeFilter('thisYear')} label={'This Year'} selectState={selectedBtn === "thisYear"} />
                                </div>

                                <div className="col-span-12 md:col-span-4 flex justify-start md:justify-end">
                                    <form onSubmit={handleFilterSubmit} className="w-full md:w-auto">
                                        <div className="grid grid-cols-2 md:flex gap-3">
                                            <input
                                                type="date"
                                                name="startDate"
                                                value={startDate}
                                                onChange={handleFormDate}
                                                min="2023-01-01"
                                                max={new Date().toISOString().split('T')[0]}
                                                className={`w-full px-4 py-2 rounded-lg shadow-sm border cursor-pointer bg-white ${selectedBtn === "range" ? 'border-blue-500 ring-2 ring-blue-100 text-main-blue' : 'border-gray-300 text-gray-700 hover:border-blue-300'}`}
                                            />
                                            <input
                                                type="date"
                                                name="endDate"
                                                value={endDate}
                                                onChange={handleFormDate}
                                                min="2023-01-01"
                                                max={new Date().toISOString().split('T')[0]}
                                                className={`w-full px-4 py-2 rounded-lg shadow-sm border cursor-pointer bg-white ${selectedBtn === "range" ? 'border-blue-500 ring-2 ring-blue-100 text-main-blue' : 'border-gray-300 text-gray-700 hover:border-blue-300'}`}
                                            />
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                        <DataTypeFilter />
                    </div>

                    {/* Dashboard Content */}
                    <div className='p-6 bg-main-white rounded-lg mt-1'>
                        {dataFetchError ? (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl shadow-sm mb-6">
                                <div className="flex items-center">
                                    <svg className="w-6 h-6 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                    <span className="text-sm font-medium">{dataFetchError}</span>
                                </div>
                            </div>
                        ) : hasData() ? (
                            <>
                                {renderCards()}
                                {renderCharts()}
                            </>
                        ) : (
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                                <NoDataFound
                                    title={getNoDataMessage().title}
                                    message={getNoDataMessage().message}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default Home;